import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";
import {
  Card,
  Table,
  Col,
  Button,
  Row,
  Form,
  Modal,
  Spinner,
  ButtonGroup,
} from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  useEmployeeWorkLocation,
  useEmployeeDropdown,
} from "../../actions/employee-actions";
import { useGetProjectByUUId } from "../../actions/project-action";
import { useGetEmployeeInfoDetailByUUID } from "../../actions/employee-actions";
import {
  usePostTimesheet,
  useGetTimesheetByUUID,
} from "../../actions/timesheet-actions";
import { TostifyConfig } from "../../utils/Constance";
import { toast } from "react-toastify";
import { IsValidPermissions } from "../../services/AuthService";
import { ServiceContext } from "../../context/ServiceContext";
import TimePicker from "rc-time-picker";
import "rc-time-picker/assets/index.css";
import moment from "moment";
import LoadingOverlay from "react-loading-overlay";
import { format, parseISO } from "date-fns";
import { useGetEmployeeWorkDaysDetail } from "../../actions/timesheet-actions";
import { usePostBulkUpload } from "../../actions/timesheet-actions";

// ===== Utility helpers exported for unit testing =====
export const to24HourString = (time) => {
  if (!time) return null;
  if (moment.isMoment(time)) return time.format("HH:mm");
  return moment(time, ["HH:mm", "H:mm", "h:mm A", "HH:mm:ss"]).format("HH:mm");
};

export const minutesBetween = (startHHmm, endHHmm) => {
  if (!startHHmm || !endHHmm) return 0;
  const start = moment(startHHmm, "HH:mm");
  let end = moment(endHHmm, "HH:mm");
  if (end.isBefore(start)) end = end.add(1, "day"); // overnight
  return end.diff(start, "minutes");
};

export const calculateHoursFromMinutes = (minutes) => {
  if (!minutes || minutes <= 0) return "0.00";
  
  // Calculate hours and minutes separately
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  // Format as hours.minutes (e.g., 8.15 for 8 hours 15 minutes)
  // We need to ensure minutes are always 2 digits
  const formattedMinutes = remainingMinutes.toString().padStart(2, '0');
  
  return `${hours}.${formattedMinutes}`;
};

export const calculateTotalHours = (startTime, endTime) => {
  // returns string like "8.15" for 8 hours 15 minutes
  if (!startTime || !endTime) return "0.00";
  const start = to24HourString(startTime);
  const end = to24HourString(endTime);
  const minutes = minutesBetween(start, end);
  return calculateHoursFromMinutes(minutes);
};

function TimeSheetView() {
  const { isAdminUser, getEmployeeUUID } = useContext(ServiceContext);

  const FormRef = useRef(null);

  const [showModal, setShowModal] = useState(false);

  const [getProjectName, setProjectName] = useState("");

  const [getEmployeeUUid, setEmployeeUUid] = useState("");

  const [employeeProfileData, setEmployeeProfileData] = useState({});

  const [getProjectTimesheetData, setProjectTimesheetData] = useState([]);

  const [modalData, setModalData] = useState({
    date: "",
    end_date: "",
    start_time: moment("09:00", "HH:mm"),
    end_time: moment("17:00", "HH:mm"),
    total_hours: "8.00",
    projects: null,
    resource: null,
    reporter: null,
    location: null,
  });

  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadData, setBulkUploadData] = useState({
    start_date: "",
    end_date: "",
    start_time: "09:00",
    end_time: "18:00",
    total_hours: "8.00",
    location: "",
    monday_required: false,
    tuesday_required: false,
    wednesday_required: false,
    thursday_required: false,
    friday_required: false,
    saturday_required: false,
    sunday_required: false,
  });

  const [bulkUploadErrors, setBulkUploadErrors] = useState({});

  const { data: getEmployeeInfo, isLoading: loadingEmployeeInfo } =
    useEmployeeDropdown();

  const { data: getEmployeeWorkLocation } = useEmployeeWorkLocation();

  const {
    data: getEmployeeProjectsByUUID,
    isFetching: LoadingEmployeeProjectsByUUID,
  } = useGetProjectByUUId(getEmployeeUUid);

  const {
    data: getEmployeeWorkDaysByUUID,
    isFetching: LoadingEmployeeWorkDaysByUUID,
  } = useGetEmployeeWorkDaysDetail(getEmployeeUUid);

  const {
    data: getEmployeeDetailByUUID,
    isLoading: LoadingEmployeeDetailByUUID,
  } = useGetEmployeeInfoDetailByUUID(getEmployeeUUid);

  const {
    data: getEmployeeTimesheetByUUID,
    refetch: refetchEmployeeTimesheet,
    isFetching: LoadingEmployeeTimeSheet,
  } = useGetTimesheetByUUID(getEmployeeUUid, getProjectName);

  const { mutate: postTimesheetFn, isLoading: postTimesheetLoading } =
    usePostTimesheet();

  const { mutate: postBulkUpload, isLoading: bulkuploadLoading } =
    usePostBulkUpload();

  // Open bulk modal and load locations (async action may return a promise or data)
  const [locationList, setLocationList] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const openBulkUploadModal = async () => {
    setShowBulkUploadModal(true);
    setLoadingLocations(true);
    setBulkUploadErrors({});
    try {
      const locations = await getEmployeeWorkLocation;
      setLocationList(locations || []);
    } catch (err) {
      console.error("Error loading locations:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const closeBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setBulkUploadData({
      start_date: "",
      end_date: "",
      start_time: "09:00",
      end_time: "18:00",
      total_hours: "8.00",
      location: "",
      monday_required: false,
      tuesday_required: false,
      wednesday_required: false,
      thursday_required: false,
      friday_required: false,
      saturday_required: false,
      sunday_required: false,
    });
    setBulkUploadErrors({});
  };

  const validateBulkUploadData = (data) => {
    const errors = {};

    if (!data.start_date) errors.start_date = "Start date is required";
    if (!data.end_date) errors.end_date = "End date is required";

    if (data.start_date && data.end_date) {
      const s = moment(data.start_date);
      const e = moment(data.end_date);
      if (e.isBefore(s)) errors.dateRange = "End date must be on or after start date";
    }

    if (!data.start_time) errors.start_time = "Start time is required";
    if (!data.end_time) errors.end_time = "End time is required";

    if (data.start_time && data.end_time) {
      const minutes = minutesBetween(data.start_time, data.end_time);
      if (minutes <= 0) errors.timeRange = "Total hours must be greater than 0";
    }

    if (!data.location) errors.location = "Location is required";

    return errors;
  };

  const handleBulkUploadChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newData = {
      ...bulkUploadData,
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "start_time" || name === "end_time") {
      if (newData.start_time && newData.end_time) {
        newData.total_hours = calculateTotalHours(newData.start_time, newData.end_time);
      } else {
        newData.total_hours = "0.00";
      }
    }

    const errors = validateBulkUploadData(newData);
    setBulkUploadErrors(errors);
    setBulkUploadData(newData);
  };

  const isDateRangeValid =
    bulkUploadData.start_date &&
    bulkUploadData.end_date &&
    moment(bulkUploadData.end_date).isSameOrAfter(moment(bulkUploadData.start_date));

  const isUploadDisabled =
    !bulkUploadData.start_date ||
    !bulkUploadData.end_date ||
    !isDateRangeValid ||
    !bulkUploadData.start_time ||
    !bulkUploadData.end_time ||
    !bulkUploadData.total_hours ||
    parseFloat(bulkUploadData.total_hours) <= 0 ||
    !bulkUploadData.location ||
    !getProjectName ||
    Object.keys(bulkUploadErrors).length > 0;

  const showBulkUploadResults = (response) => {
    if (response.created_count > 0 && response.skipped_count === 0) {
      return (
        <div>
          <p>Successfully created {response.created_count} entries.</p>
        </div>
      );
    } else if (response.created_count > 0 && response.skipped_count > 0) {
      return (
        <div>
          <p>Successfully created {response.created_count} entries.</p>
          <p className="text-warning">{response.skipped_count} entries skipped (already existed).</p>
        </div>
      );
    } else if (response.skipped_count > 0 && response.created_count === 0) {
      return (
        <div>
          <p className="text-warning">All selected dates already have timesheet entries.</p>
          {response.skipped_entries.length > 0 && (
            <div className="mt-2">
              <p>Skipped dates:</p>
              <ul>
                {response.skipped_entries.map((entry, index) => (
                  <li key={index}>{entry.date}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    return <p>No entries were created.</p>;
  };

  const handleBulkUploadSubmit = () => {
    const errors = validateBulkUploadData(bulkUploadData);
    if (Object.keys(errors).length > 0) {
      setBulkUploadErrors(errors);
      return;
    }

    const payload = {
      date: bulkUploadData.start_date,
      end_date: bulkUploadData.end_date,
      start_time: bulkUploadData.start_time,
      end_time: bulkUploadData.end_time,
      total_hours: bulkUploadData.total_hours,
      projects: getEmployeeProjectsByUUID?.find((p) => p.project_name === getProjectName)?.project,
      resource: getEmployeeDetailByUUID?.id,
      reporter: getEmployeeDetailByUUID?.direct_manager,
      location: bulkUploadData.location,
      monday_required: bulkUploadData.monday_required,
      tuesday_required: bulkUploadData.tuesday_required,
      wednesday_required: bulkUploadData.wednesday_required,
      thursday_required: bulkUploadData.thursday_required,
      friday_required: bulkUploadData.friday_required,
      saturday_required: bulkUploadData.saturday_required,
      sunday_required: bulkUploadData.sunday_required,
    };

    postBulkUpload(payload, {
      onSuccess: (response) => {
        if (response.created_count > 0) {
          toast.success(showBulkUploadResults(response), { ...TostifyConfig, autoClose: 8000 });
        } else {
          toast.warn(showBulkUploadResults(response), { ...TostifyConfig, autoClose: 8000 });
        }
        closeBulkUploadModal();
        refetchEmployeeTimesheet();
      },
      onError: (error) => {
        toast.error("Failed to bulk upload timesheet entries", TostifyConfig);
        console.error("Bulk upload error:", error);
      },
    });
  };

  const handelModalButtonClick = () => {
    const formattedStartTime = to24HourString(modalData.start_time);
    const formattedEndTime = to24HourString(modalData.end_time);
    if (
      parseFloat(modalData.total_hours) > 0 &&
      modalData.start_time &&
      modalData.end_time &&
      modalData.date &&
      modalData.end_date &&
      modalData.projects &&
      modalData.resource &&
      modalData.location
    ) {
      postTimesheetFn(
        {
          ...modalData,
          start_time: formattedStartTime,
          end_time: formattedEndTime,
        },
        {
          onSuccess: () => {
            toast.success("TimeSheet Updated!!", TostifyConfig);
            setShowModal(false);
            refetchEmployeeTimesheet();
          },
        }
      );
    }
  };

  const employeeSelectChangeHandler = (e) => {
    const selectedIndex = e.target.selectedIndex;
    const selectName = e.target.name;
    const selectValue = e.target.options[selectedIndex].value;
    setEmployeeProfileData({ ...employeeProfileData, [selectName]: selectValue });
    setProjectName(null);
  };

  const projectSelectChangeHandler = (e) => {
    const selectedIndex = e.target.selectedIndex;
    const selectValue = e.target.options[selectedIndex].value;
    const selectedLabel = e.target.options[selectedIndex].label;
    setModalData((prev) => ({ ...prev, projects: selectValue }));
    setProjectName(selectedLabel);
  };

  const handleEventClick = (info) => {
    console.log("Event clicked:", info.event);
  };

  const eventContent = (arg) => {
    const hasMultipleDays = arg.event.end && moment(arg.event.end).diff(moment(arg.event.start), 'days') > 1;
    return (
      <div>
        <strong>Project Name:</strong>{" "}
        <span dangerouslySetInnerHTML={{ __html: arg.event.title }} />
        <br />
        <strong>Total Hours:</strong>{" "}
        <span dangerouslySetInnerHTML={{ __html: arg.event._def.extendedProps.total_hours }} />
        {hasMultipleDays && (
          <>
            <br />
            <small>{moment(arg.event.start).format('MMM D')} - {moment(arg.event.end).subtract(1, 'day').format('MMM D')}</small>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    const eventTimestamp = getEmployeeTimesheetByUUID?.map((e) => {
      const hasEndDate = e.end_date && e.end_date !== e.date;
      return {
        title: e.project_name,
        date: e.date,
        end: hasEndDate ? moment(e.end_date).add(1, 'day').format('YYYY-MM-DD') : undefined,
        timeText: e.date,
        total_hours: typeof e.total_hours === 'number' ? e.total_hours.toFixed(2) : e.total_hours,
        backgroundColor: "rgb(72,61,139)",
        extendedProps: { start_date: e.date, end_date: e.end_date, original_total_hours: e.total_hours },
      };
    }) || [];
    setProjectTimesheetData(eventTimestamp);
  }, [getEmployeeTimesheetByUUID]);

  useEffect(() => {
    if (getEmployeeUUid && getProjectName) {
      refetchEmployeeTimesheet();
    } else {
      setProjectTimesheetData([]);
    }
  }, [getEmployeeUUid, getProjectName, refetchEmployeeTimesheet]);

  useEffect(() => {
    if (getEmployeeDetailByUUID) {
      setModalData((prev) => ({ ...prev, reporter: getEmployeeDetailByUUID?.direct_manager, resource: getEmployeeDetailByUUID?.id }));
    }
  }, [getEmployeeDetailByUUID]);

  useEffect(() => {
    if (isAdminUser === false) setEmployeeUUid(getEmployeeUUID);
  }, [getEmployeeUUID, isAdminUser]);

  useEffect(() => {
    if (employeeProfileData?.employee) setEmployeeUUid(employeeProfileData?.employee);
  }, [employeeProfileData?.employee]);

  const bulkUploadButtonDisabled = !(employeeProfileData?.employee && getEmployeeDetailByUUID?.direct_manager_name && (getEmployeeDetailByUUID?.first_name || getEmployeeDetailByUUID?.last_name) && getProjectName);

  // handle calendar date click moved inside component to keep hooks order
  function handleDateClick(arg) {
    const FormValidity = FormRef.current?.reportValidity();
    const existingEntry = getProjectTimesheetData?.some((item) => item.date === arg.dateStr);
    if (FormValidity && !existingEntry) {
      const clickedDay = arg.date.getDay();
      const apiDayNumber = clickedDay === 0 ? 1 : clickedDay + 1;
      const workDay = getEmployeeWorkDaysByUUID?.find((d) => d.day === apiDayNumber);
      const startMoment = workDay?.start_time ? moment(workDay.start_time, "HH:mm:ss") : moment("09:00", "HH:mm");
      const endMoment = workDay?.end_time ? moment(workDay.end_time, "HH:mm:ss") : null;
      let totalHours = "0.00";
      if (startMoment && endMoment) {
        totalHours = calculateTotalHours(startMoment.format('HH:mm'), endMoment.format('HH:mm'));
      }
      setModalData({ ...modalData, date: arg.dateStr, end_date: arg.dateStr, start_time: startMoment, end_time: endMoment, total_hours: totalHours });
      setShowModal(true);
    }
  }

  return (
    <Fragment>
      <div>
        {(IsValidPermissions("timesheet.add_timesheet") || IsValidPermissions("timesheet.can_add_employee_timesheet")) && (
          <div className="col-xl-12 col-xxl-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">
                  <i className="fa fa-newspaper font-18 align-middle me-2 text-primary"></i>&nbsp; Add Timesheet
                </h4>
                {isAdminUser && (
                  <Button variant="secondary" size="sm" onClick={openBulkUploadModal} disabled={bulkUploadButtonDisabled}>Add Timesheet</Button>
                )}
              </div>
              <div className="card-body">
                <form ref={FormRef}>
                  <Form.Group>
                    <Row>
                      {IsValidPermissions("timesheet.add_timesheet") && (
                        <Col className="col-lg">
                          <label className="mb-2">Employee</label>
                          <Form.Select name="employee" className="form-control" value={employeeProfileData.employee || ""} onChange={employeeSelectChangeHandler} disabled={loadingEmployeeInfo} required>
                            <option value="">Select Employee</option>
                            {getEmployeeInfo?.map((option) => (
                              <option key={option.id} value={option.employee_uuid}>{option.first_name} {option.last_name}{option.employee_number ? ` (${option.employee_number})` : ""}</option>
                            ))}
                          </Form.Select>
                        </Col>
                      )}
                      <Col className="col-md-6">
                        <label className="mb-2">Project</label>
                        <Form.Select className="form-control mb-0" name="client_master" required disabled={LoadingEmployeeProjectsByUUID || LoadingEmployeeDetailByUUID} onChange={projectSelectChangeHandler}>
                          <option value="">Select Project</option>
                          {LoadingEmployeeProjectsByUUID ? (
                            <option className="text-muted" value="" disabled selected>Fetching...</option>
                          ) : (
                            getEmployeeProjectsByUUID?.map((option) => (
                              <option key={option.project} value={option.project}>{option.project_name}</option>
                            ))
                          )}
                        </Form.Select>
                      </Col>
                    </Row>
                    <br />
                    <Row>
                      <Col className="col-lg">
                        <label>Reporter</label>
                        <Form.Control className="mb-0" type="text" value={getEmployeeDetailByUUID?.direct_manager_name || ""} disabled />
                      </Col>
                      <Col className="col-lg mg-t-10 mg-lg-t-0">
                        <label>Assignee</label>
                        <Form.Control className="mb-0" type="text" value={`${getEmployeeDetailByUUID?.first_name || ""} ${getEmployeeDetailByUUID?.last_name || ""}`} disabled />
                      </Col>
                    </Row>
                  </Form.Group>
                </form>
              </div>
            </div>

            <Modal show={showBulkUploadModal} backdrop="static" onHide={closeBulkUploadModal} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Timesheet Entries (Bulk)</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {loadingLocations ? (
                  <div className="text-center py-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading locations...</p></div>
                ) : (
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Location</Form.Label>
                          <Form.Select name="location" value={bulkUploadData.location} onChange={handleBulkUploadChange} required isInvalid={!!bulkUploadErrors.location}>
                            <option value="">Select Location</option>
                            {locationList.map((loc) => <option key={loc.id} value={loc.id}>{loc.location_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{bulkUploadErrors.location}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Total Hours</Form.Label>
                          <Form.Control type="text" name="total_hours" value={bulkUploadData.total_hours} readOnly className="bg-light" isInvalid={parseFloat(bulkUploadData.total_hours) <= 0} />
                          {parseFloat(bulkUploadData.total_hours) <= 0 && (<Form.Control.Feedback type="invalid">Total hours must be greater than 0</Form.Control.Feedback>)}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control type="date" name="start_date" value={bulkUploadData.start_date} onChange={handleBulkUploadChange} max={moment().format("YYYY-MM-DD")} required isInvalid={!!bulkUploadErrors.dateRange} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control type="date" name="end_date" value={bulkUploadData.end_date} onChange={handleBulkUploadChange} max={moment().format("YYYY-MM-DD")} min={bulkUploadData.start_date} required isInvalid={!!bulkUploadErrors.dateRange} />
                          {bulkUploadErrors.dateRange && (<div className="text-danger small mt-1">{bulkUploadErrors.dateRange}</div>)}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Time </Form.Label>
                          <Form.Control type="time" name="start_time" value={bulkUploadData.start_time} onChange={handleBulkUploadChange} required isInvalid={!!bulkUploadErrors.start_time || !!bulkUploadErrors.timeRange} />
                          {bulkUploadErrors.start_time && (<Form.Control.Feedback type="invalid">{bulkUploadErrors.start_time}</Form.Control.Feedback>)}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Time </Form.Label>
                          <Form.Control type="time" name="end_time" value={bulkUploadData.end_time} onChange={handleBulkUploadChange} required isInvalid={!!bulkUploadErrors.end_time || !!bulkUploadErrors.timeRange} />
                          {bulkUploadErrors.end_time && (<Form.Control.Feedback type="invalid">{bulkUploadErrors.end_time}</Form.Control.Feedback>)}
                          {bulkUploadErrors.timeRange && (<div className="text-danger small mt-1">{bulkUploadErrors.timeRange}</div>)}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12} className="mb-3">
                        <Form.Label>Include Days</Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                          <Form.Check inline type="checkbox" id="monday_required" label="Mon" name="monday_required" checked={bulkUploadData.monday_required} onChange={handleBulkUploadChange} />
                          <Form.Check inline type="checkbox" id="tuesday_required" label="Tue" name="tuesday_required" checked={bulkUploadData.tuesday_required} onChange={handleBulkUploadChange} />
                          <Form.Check inline type="checkbox" id="wednesday_required" label="Wed" name="wednesday_required" checked={bulkUploadData.wednesday_required} onChange={handleBulkUploadChange} />
                          <Form.Check inline type="checkbox" id="thursday_required" label="Thu" name="thursday_required" checked={bulkUploadData.thursday_required} onChange={handleBulkUploadChange} />
                          <Form.Check inline type="checkbox" id="friday_required" label="Fri" name="friday_required" checked={bulkUploadData.friday_required} onChange={handleBulkUploadChange} />
                          <Form.Check inline type="checkbox" id="saturday_required" label="Sat" name="saturday_required" checked={bulkUploadData.saturday_required} onChange={handleBulkUploadChange} />
                          <Form.Check inline type="checkbox" id="sunday_required" label="Sun" name="sunday_required" checked={bulkUploadData.sunday_required} onChange={handleBulkUploadChange} />
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-3 p-3 bg-light rounded">
                      <h6>Summary:</h6>
                      <ul className="mb-0">
                        <li>Project: <strong>{getProjectName || "Not selected"}</strong></li>
                        <li>Employee: <strong>{getEmployeeDetailByUUID?.first_name || ""} {getEmployeeDetailByUUID?.last_name || ""}</strong></li>
                        <li>Reporter: <strong>{getEmployeeDetailByUUID?.direct_manager_name || ""}</strong></li>
                        <li>Work Hours: <strong>{bulkUploadData.start_time} to {bulkUploadData.end_time} ({bulkUploadData.total_hours} hours)</strong></li>
                      </ul>
                    </div>
                  </Form>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" size="sm" disabled={isUploadDisabled || bulkuploadLoading} onClick={handleBulkUploadSubmit}>
                  {bulkuploadLoading ? (<><Spinner animation="border" size="sm" className="me-2" />Uploading...</>) : ("Upload")}
                </Button>
                <Button variant="secondary" size="sm" onClick={closeBulkUploadModal} disabled={bulkuploadLoading}>Cancel</Button>
              </Modal.Footer>
            </Modal>

            <ModalComponent
              showModal={showModal}
              setShowModal={setShowModal}
              modalData={modalData}
              setModalData={setModalData}
              getEmployeeWorkLocation={getEmployeeWorkLocation}
              handelModalButtonClick={handelModalButtonClick}
              postTimesheetLoading={postTimesheetLoading}
            />

            <div className="card">
              <div className="card-body">
                <LoadingOverlay active={LoadingEmployeeTimeSheet} spinner text="Fetching..." className="custom-overlay">
                  <FullCalendar plugins={[dayGridPlugin, interactionPlugin]} initialView="dayGridMonth" weekends={true} dateClick={handleDateClick} events={getProjectTimesheetData} eventContent={eventContent} eventClick={handleEventClick} />
                </LoadingOverlay>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}

function ModalComponent(props) {
  const ModalFormRef = useRef(null);
  const totalHoursInputRef = useRef(null);
  const [modalValidation, setModalValidation] = useState("");
  const [timeValidation, setTimeValidation] = useState("");

  let closeModal = () => { props.setShowModal(false); };

  const validateModalData = (data) => {
    if (data.start_time && data.end_time) {
      if (data.date && data.end_date) {
        const startISO = `${data.date} ${moment.isMoment(data.start_time) ? data.start_time.format('HH:mm') : to24HourString(data.start_time)}`;
        const endISO = `${data.end_date} ${moment.isMoment(data.end_time) ? data.end_time.format('HH:mm') : to24HourString(data.end_time)}`;
        const startMoment = moment(startISO, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(endISO, 'YYYY-MM-DD HH:mm');
        if (endMoment.isSameOrBefore(startMoment)) return "End date/time must be after start date/time";
        const minutes = endMoment.diff(startMoment, 'minutes');
        const totalHours = minutes > 0 ? Math.round((minutes / 60) * 100) / 100 : 0;
        if (totalHours <= 0) return "Total hours must be greater than 0";
      } else {
        const start = moment.isMoment(data.start_time) ? data.start_time : moment(data.start_time, 'HH:mm');
        let end = moment.isMoment(data.end_time) ? data.end_time : moment(data.end_time, 'HH:mm');
        if (end.isBefore(start)) {
          // Allow overnight only if end_date is set - otherwise treat as invalid
          return "End time must be after start time or set end date for overnight shift";
        }
        const minutes = end.diff(start, 'minutes');
        if (minutes <= 0) return "Total hours must be greater than 0";
      }
    }
    return "";
  };

  const handleButtonClick = () => {
    const FormValidity = ModalFormRef.current?.reportValidity();
    const timeError = validateModalData(props.modalData);
    if (timeError) { setTimeValidation(timeError); return; }
    if (FormValidity === true) props.handelModalButtonClick();
  };

  const calculateTotalHoursWithDates = (startDate, endDate, startTime, endTime) => {
  if (!startDate || !endDate || !startTime || !endTime) return "0.00";
  const s = moment.isMoment(startTime) ? startTime.format('HH:mm') : to24HourString(startTime);
  const e = moment.isMoment(endTime) ? endTime.format('HH:mm') : to24HourString(endTime);
  const startMoment = moment(`${startDate} ${s}`, 'YYYY-MM-DD HH:mm');
  const endMoment = moment(`${endDate} ${e}`, 'YYYY-MM-DD HH:mm');
  const minutes = endMoment.diff(startMoment, 'minutes');
  
  if (minutes <= 0) {
    setTimeValidation("End date/time must be after start date/time");
    return "0.00";
  }
  
  setTimeValidation("");
  
  // Calculate hours and minutes separately
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  // Format as hours.minutes (e.g., 8.15 for 8 hours 15 minutes)
  const formattedMinutes = remainingMinutes.toString().padStart(2, '0');
  
  return `${hours}.${formattedMinutes}`;
};

  const handleTimeChange = (key, value) => {
    const newData = { ...props.modalData, [key]: value };
    if (newData.start_time && newData.end_time && newData.date && newData.end_date) {
      newData.total_hours = calculateTotalHoursWithDates(newData.date, newData.end_date, newData.start_time, newData.end_time);
    } else {
      // if either date missing, try simple minutesBetween iff both times present and same day
      if (newData.start_time && newData.end_time && newData.date && !newData.end_date) {
        const mins = minutesBetween(to24HourString(newData.start_time), to24HourString(newData.end_time));
        newData.total_hours = calculateHoursFromMinutes(mins);
      } else {
        newData.total_hours = "0.00";
      }
    }
    props.setModalData(newData);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...props.modalData, [name]: value };
    if (newData.start_time && newData.end_time && newData.date && newData.end_date) {
      newData.total_hours = calculateTotalHoursWithDates(newData.date, newData.end_date, newData.start_time, newData.end_time);
    }
    props.setModalData(newData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "total_hours" && parseFloat(value) <= 0) setModalValidation("Invalid Total Hours"); else setModalValidation("");
    props.setModalData({ ...props.modalData, [name]: value });
  };

  const employeeLocationSelectChangeHandler = (e) => {
    const selectedIndex = e.target.selectedIndex;
    const selectName = e.target.name;
    const selectValue = e.target.options[selectedIndex].value;
    props.setModalData({ ...props.modalData, [selectName]: selectValue });
  };

  return (
    <Modal show={props.showModal} backdrop="static" onHide={() => closeModal()}>
      <Modal.Header closeButton>
        <Modal.Title>Add Total Working Hours</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5><strong>Start Date: {props.modalData.date}</strong></h5>
        <br />
        <form ref={ModalFormRef}>
          <Row>
            <Col className="col-lg-12">
              <label>End Date</label>
              <Form.Control type="date" name="end_date" value={props.modalData.end_date} onChange={handleDateChange} className="mb-4" min={props.modalData.date} max={moment(props.modalData.date).add(1, 'day').format('YYYY-MM-DD')} required />
            </Col>
          </Row>

          <Row>
            <Col className="col-lg">
              <label>Work Location</label>
              <Form.Select name="location" value={props.modalData.location || ""} onChange={employeeLocationSelectChangeHandler} className="mb-4" required>
                <option value="">Select Location</option>
                {props.getEmployeeWorkLocation?.map((option) => (<option key={option.id} value={option.id}>{option.location_name}</option>))}
              </Form.Select>
            </Col>
          </Row>

          <Row>
            <Col className="col-lg-6">
              <label>Start Time</label>
              <TimePicker showSecond={false} minuteStep={1} format="HH:mm" value={props.modalData.start_time} onChange={(value) => handleTimeChange('start_time', value)} className="form-control mb-4" placeholder="Start Time" />
            </Col>
            <Col className="col-lg-6">
              <label>End Time</label>
              <TimePicker showSecond={false} minuteStep={1} format="HH:mm" value={props.modalData.end_time} onChange={(value) => handleTimeChange('end_time', value)} className="form-control mb-4" placeholder="End Time" />
            </Col>
          </Row>

          {timeValidation && (<div className="alert alert-danger mb-4" role="alert">{timeValidation}</div>)}

          <Row>
            <Col className="col-lg">
              <label>Total Hours</label>
              <Form.Control type="text" onChange={handleInputChange} name="total_hours" value={typeof props.modalData.total_hours === 'number' ? props.modalData.total_hours.toFixed(2) : props.modalData.total_hours} ref={totalHoursInputRef} onClick={() => { if (totalHoursInputRef.current) { totalHoursInputRef.current.focus(); totalHoursInputRef.current.select(); } }} className="mb-4" required readOnly isInvalid={parseFloat(props.modalData.total_hours) <= 0} />
              {parseFloat(props.modalData.total_hours) <= 0 && (<Form.Control.Feedback type="invalid">Total hours must be greater than 0</Form.Control.Feedback>)}
              <div className="text-danger">{modalValidation}</div>
            </Col>
          </Row>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary btn-sm" onClick={handleButtonClick} disabled={props.postTimesheetLoading || parseFloat(props.modalData.total_hours) <= 0 || !!timeValidation}>
          {props.postTimesheetLoading ? (<><Spinner animation="border" size="sm" className="me-2" />Loading...</>) : ("Save")}
        </Button>
        <Button variant="danger light btn-sm" onClick={() => closeModal()}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TimeSheetView;

